import _round from 'lodash/round';
import { Suspense, useCallback, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import { LightningBoltIcon } from '@/components/icons/lightning-bolt';
import { CreateHiringGoalButton } from '@/components/pages/channels/create-hiring-goal-button';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { useBudgetTargetSignal } from '@/contexts/budget-target-signal-context';
import {
  useOpeningAnalytics,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useLastAutomationChannel } from '@/hooks/use-last-automation-channel';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { useShowHiringGoalEmptyState } from '@/components/pages/channels/use-show-hiring-goal-empty-state';
import { cn } from '@/utils/classnames';
import { isWithinLastDays, toDate } from '@/utils/date-util';
import { reportBoundaryError } from '@/utils/sentry';

import { PerformanceStatusBanner } from '../performance-status-banner';
import { BUDGET_THRESHOLD_PERCENTAGE } from './constants';
import {
  dismissalKey,
  isDismissalActive,
  omitAutomationBannerDismissals,
  staleKeysForTransition,
  useAutomationBannerState,
} from './use-automation-banner-state';
import { useAutomationMessages } from './use-automation-messages';
import { ViewSourcingToggleButton } from '../view-sourcing-toggle-button';

const BANNER_DISMISSAL_LOOKBACK_DAYS = 5;

function AutomationStatusBannerContent() {
  const openingSlug = useOpeningSlug();
  // `useLastAutomationChannel` kicks off its sibling fetch before suspending,
  // so call it first to keep the channels-list query in parallel with the
  // funnel-details API.
  const channel = useLastAutomationChannel();
  const { opening, hiringGoal, recentPastHiringGoal, hiringGoalEditUrl } =
    useOpeningDetails();
  const { analyticsData } = useOpeningAnalytics();
  const showHiringGoalEmptyState = useShowHiringGoalEmptyState(hiringGoal);
  const isCurrentHiringGoal =
    ((hiringGoal?.external_id && hiringGoal?.is_current) ?? false) ||
    (recentPastHiringGoal?.is_current ?? false);
  const { getAutomationMessages } = useAutomationMessages();
  const { requestBudgetTargetOpen } = useBudgetTargetSignal();
  const { automationBannerClosed, setAutomationBannerClosed } =
    useAutomationBannerState();

  const lastAutomationReason = channel?.deactivation_reason ?? null;
  const isActive = !channel?.deactivated_at;
  const lastAutomationTriggerAt = toDate(
    channel?.automation_triggered_at ?? null,
  );
  const bannerColor = isActive ? 'blue' : 'rose';

  /* Scroll to the Spend & Cost card and auto-open the budget target popover */
  const handleUpdateBudget = useCallback(() => {
    requestBudgetTargetOpen();
  }, [requestBudgetTargetOpen]);

  /* Check if the spend has breached the 80% threshold of the budget, and is less than the budget */
  const { hasSpendBreachedThreshold, spendPercentage } = useMemo(() => {
    const budget = analyticsData?.target_budget ?? 0;
    const spend = analyticsData?.spend_stats?.total ?? 0;

    if (!budget || !spend)
      return { hasSpendBreachedThreshold: false, spendPercentage: undefined };

    const hasSpendBreachedThreshold =
      spend < budget && spend > budget * BUDGET_THRESHOLD_PERCENTAGE;
    const spendPercentage = hasSpendBreachedThreshold
      ? _round((spend / budget) * 100, 1)
      : undefined;

    return { hasSpendBreachedThreshold, spendPercentage };
  }, [analyticsData]);

  const automationReason = useMemo(
    () =>
      !lastAutomationReason && hasSpendBreachedThreshold
        ? 'target_budget_breached'
        : lastAutomationReason,
    [lastAutomationReason, hasSpendBreachedThreshold],
  );

  const currentDismissalKey = useMemo(
    () => dismissalKey(automationReason, isActive),
    [automationReason, isActive],
  );

  const isDismissed = useMemo(() => {
    if (currentDismissalKey == null) return false;
    const byReason = automationBannerClosed[openingSlug];
    return isDismissalActive(byReason?.[currentDismissalKey]);
  }, [automationBannerClosed, openingSlug, currentDismissalKey]);

  /* Get automation messages */
  const automationCopy = useMemo(() => {
    const copy = getAutomationMessages(
      automationReason,
      lastAutomationTriggerAt,
      spendPercentage,
    );

    if (!copy) return null;

    const title = isActive ? copy.activeTitle : copy.pausedTitle;
    const description = isActive
      ? copy.activeDescription
      : copy.pausedDescription;

    return {
      title,
      description,
    };
  }, [
    automationReason,
    getAutomationMessages,
    isActive,
    lastAutomationTriggerAt,
    spendPercentage,
  ]);

  /* Button logic */
  const button = useMemo(() => {
    if (
      lastAutomationReason === 'hiring_goal_ended' &&
      !isActive &&
      hiringGoal?.external_id
    ) {
      return (
        <>
          {!opening.hasActiveHiringGoal && (
            <CreateHiringGoalButton hiringGoalUrl={hiringGoalEditUrl} />
          )}
          <ViewSourcingToggleButton />
        </>
      );
    }

    if (lastAutomationReason === 'target_budget_met' && !isActive) {
      return (
        <Button variant="secondary" onClick={handleUpdateBudget}>
          <FormattedMessage
            defaultMessage="Update Budget"
            description="CTA when spend target was hit: scroll to the Spend & Cost card and open the budget target popover"
          />
        </Button>
      );
    }

    return null;
  }, [
    lastAutomationReason,
    isActive,
    hiringGoal?.external_id,
    hiringGoalEditUrl,
    handleUpdateBudget,
    opening.hasActiveHiringGoal,
  ]);

  const closeBanner = useCallback(() => {
    if (currentDismissalKey == null) return;
    setAutomationBannerClosed(prev => ({
      ...prev,
      [openingSlug]: {
        ...(prev[openingSlug] ?? {}),
        [currentDismissalKey]: Date.now(),
      },
    }));
  }, [currentDismissalKey, openingSlug, setAutomationBannerClosed]);

  /* Campaign reactivated or paused: clear stale dismissals for the opposite state. */
  useEffect(() => {
    const keysToRemove = staleKeysForTransition(isActive);
    setAutomationBannerClosed(prev => {
      const opening = prev[openingSlug];
      if (!opening) return prev;
      const hasStale = keysToRemove.some(k => k in opening);
      if (!hasStale) return prev;
      return omitAutomationBannerDismissals(prev, openingSlug, keysToRemove);
    });
  }, [isActive, openingSlug, setAutomationBannerClosed]);

  if (showHiringGoalEmptyState) {
    return null;
  }

  /* For past hiring goal we should not show funnel/budget automation messages. We should show the hiring goal status banner instead. <PerformanceStatusBanner /> */
  if (!isCurrentHiringGoal && lastAutomationReason !== 'hiring_goal_ended') {
    return <PerformanceStatusBanner />;
  }

  /* If the automation was not triggered in the last 5 days, we should not show the banner. */
  if (
    !hasSpendBreachedThreshold &&
    (!lastAutomationTriggerAt ||
      !isWithinLastDays(
        lastAutomationTriggerAt,
        BANNER_DISMISSAL_LOOKBACK_DAYS,
      ))
  ) {
    return null;
  }

  /* For past hiring goal we should not show hiring automation message if there is an active hiring goal present. */
  if (
    lastAutomationReason === 'hiring_goal_ended' &&
    !isActive &&
    opening.hasActiveHiringGoal
  ) {
    return null;
  }

  /* If there is no automation copy, we should not show the banner. */
  if (!automationCopy) {
    return null;
  }

  if (isDismissed) {
    return null;
  }

  return (
    <Banner
      color={bannerColor}
      title={automationCopy.title}
      description={automationCopy.description}
      button={button}
      onClose={
        currentDismissalKey != null &&
        automationReason !== 'hiring_goal_ended' &&
        currentDismissalKey !== 'target_budget_met:paused'
          ? closeBanner
          : undefined
      }
      icon={
        <LightningBoltIcon
          className={cn('h-4 w-4', isActive ? 'text-fx-blue' : 'text-fx-rose')}
        />
      }
    />
  );
}

/**
 * Wrapped in its own Suspense boundary so its `useOpeningDetails()` call
 * doesn't block sibling sections (Analytics, ChannelsSection, etc.) in
 * `ChannelsContent` from mounting and firing their own fetches.
 */
function AutomationStatusBanner() {
  return (
    <ErrorBoundary
      onError={reportBoundaryError('channels.automation-status-banner')}
      fallback={null}
    >
      <Suspense fallback={null}>
        <AutomationStatusBannerContent />
      </Suspense>
    </ErrorBoundary>
  );
}

export { AutomationStatusBanner };
